import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowBigUp,
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Circle,
  Clock,
  GitCompare,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Scale,
  Shirt,
  Sparkles,
  User,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/male-nipple-reduction-mumbai";

const MALE_NIPPLE_FAQS = [
  {
    question: "Is male nipple reduction permanent?",
    answer:
      "Yes. The tissue removed does not regenerate. Results are permanent. Weight fluctuations can affect surrounding chest tissue over time but do not restore the removed nipple tissue.",
  },
  {
    question: "Will nipple reduction affect sensation?",
    answer:
      "Temporary changes in nipple sensation are possible in the weeks following surgery as the tissue heals. Permanent sensation loss is uncommon. Dr. Jain discusses this risk during consultation relative to the specific technique being used.",
  },
  {
    question: "Can nipple reduction be combined with gynecomastia surgery?",
    answer:
      "Yes, and this is a common and practical combination. Gynecomastia surgery removes excess breast tissue and fat from the chest. Nipple reduction refines nipple size and projection alongside it. Combining both produces complete chest contouring in a single procedure with one recovery.",
  },
  {
    question: "How long does male nipple reduction take?",
    answer:
      "30 to 60 minutes under local anesthesia. No hospital admission is required. Most patients leave the clinic within an hour of the procedure.",
  },
  {
    question: "Are there visible scars after male nipple reduction?",
    answer:
      "Scarring is minimal. Incisions are made within the natural transition between the nipple and areola, where they are not visible at normal viewing distance. They fade further over the months following surgery.",
  },
  {
    question: "When can I return to the gym after nipple reduction?",
    answer:
      "Light activity resumes within 3 to 5 days. Strenuous chest exercise, including weightlifting and upper body training, resumes after 2 to 3 weeks.",
  },
  {
    question: "Can nipple reduction fix asymmetry between sides?",
    answer:
      "Yes. Each nipple is assessed and treated independently. Different degrees of correction on each side produce bilateral symmetry. No two nipples are identical and treating them the same way produces uneven results.",
  },
  {
    question: "Is male nipple reduction safe?",
    answer:
      "Yes, when performed by a qualified plastic surgeon. Risks include temporary swelling, mild discomfort, minor asymmetry, and temporary sensation changes. Permanent complications are uncommon. Choosing an experienced surgeon significantly reduces the risk of any adverse outcome.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Male nipple reduction",
      alternateName: ["Male nipple reduction surgery", "Nipple reduction for men"],
      description:
        "Cosmetic reduction of nipple projection, diameter, or both in men under local anesthesia; often combined with gynecomastia surgery when indicated.",
      procedureType: "Surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Nipple" },
      howPerformed:
        "Local anesthesia; incisions at nipple–areola junction; tissue excision for height and/or width reduction; suture repair; 30–60 minutes; typically same-day discharge.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "typicalAgeRange", value: "18+" },
        { "@type": "PropertyValue", name: "sex", value: "Male" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: MALE_NIPPLE_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function JsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Breast", href: "/#services" },
    { name: "Male Nipple Reduction", href: "/male-nipple-reduction-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Breast</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">
              Male Nipple Reduction Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg text-deep-navy/70 mb-8">
              Nipple Reduction for Men by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Male Nipple Reduction" icon="✨" size="hero"  imageSrc={HERO.maleNippleReduction} imageAlt="Male nipple reduction"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is Male Nipple Reduction Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Male nipple reduction is a minor cosmetic procedure that reduces the size, projection, or diameter of enlarged or
              protruding nipples in men. The procedure takes 30 to 60 minutes under local anesthesia, requires no hospital admission,
              and most patients return to normal activity within 2 to 3 days. Results are permanent.
            </p>
            <p>
              Enlarged or protruding nipples in men are caused by genetics, hormonal changes during puberty, weight fluctuations, or
              gynecomastia. The result is nipples that are visible through fitted shirts, prominent during exercise or swimming, and
              a source of ongoing self-consciousness that affects clothing choices and comfort in social settings. This is a
              structural concern. It does not resolve with exercise or weight loss.
            </p>
            <p>
              Dr. Vicky Jain performs male nipple reduction as a standalone procedure and in combination with
              gynecomastia surgery for patients who have both breast tissue excess and nipple prominence. Addressing both in a
              single procedure produces a complete chest contouring result with one recovery period.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Male Nipple Reduction</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The key decision in nipple reduction is which dimension to address: projection, diameter, or both. A nipple that
                protrudes too far forward without being wide needs height reduction through tissue removal from the nipple body. A
                nipple that is wide but not particularly prominent needs width reduction. Most patients need both addressed together
                for a proportionate result. Dr. Jain assesses which dimension is creating the visible concern before planning the
                incision and tissue removal.
              </p>
              <p>
                Incisions are made on the nipple itself, positioned within the natural transition between the nipple and areola where
                they are not visible at normal viewing distance. Scarring is minimal. Sensation changes are possible in the weeks
                following surgery as the tissue heals, but permanent sensation loss is uncommon. The result is a flatter, smaller
                nipple that sits in proportion to the chest without attracting attention.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CorrectsSection() {
  const cards = [
    {
      title: "Nipples Visible Through Clothing",
      icon: Shirt,
      copy: "Protruding nipples that show through fitted shirts, gym wear, or any fabric that sits close to the chest. This is the most common concern and the primary reason men seek this procedure.",
    },
    {
      title: "Excessive Nipple Projection",
      icon: ArrowBigUp,
      copy: "Nipples that protrude significantly forward from the chest surface, creating visible prominence regardless of clothing. Height reduction removes the excess tissue and sutures the nipple flat.",
    },
    {
      title: "Wide or Enlarged Nipple Diameter",
      icon: Scale,
      copy: "Nipples that are disproportionately wide relative to the areola and chest. Width reduction removes a small wedge of tissue from the nipple circumference and closes to a smaller diameter.",
    },
    {
      title: "Nipple Asymmetry",
      icon: GitCompare,
      copy: "Uneven nipple size or projection between sides. Each nipple is assessed and treated independently to produce bilateral symmetry across the chest.",
    },
    {
      title: "Enlarged Areola",
      icon: Circle,
      copy: "When the areola is also disproportionately large alongside the nipple, areola reduction is performed simultaneously through a circular incision that reduces the pigmented area around the nipple.",
    },
    {
      title: "Nipple Prominence with Gynecomastia",
      icon: User,
      copy: "Men with gynecomastia frequently have both excess chest glandular tissue and prominent nipples. Combining gynecomastia surgery with nipple reduction addresses both in a single procedure with one recovery.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="corrects-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Male Nipple Reduction Corrects</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TYPES = [
  {
    title: "Nipple Height Reduction",
    body: "Reduces how far the nipple projects forward from the chest. Excess nipple tissue is removed from the body of the nipple and the remaining tissue is sutured flat. The most common nipple reduction technique in male patients. Appropriate for nipples with significant forward projection relative to their width.",
  },
  {
    title: "Nipple Width Reduction",
    body: "Reduces the diameter of a nipple that is wide relative to the areola and chest. A small wedge of tissue is removed from the nipple circumference and the remaining tissue is sutured to a smaller diameter. Used when the primary concern is size rather than projection.",
  },
  {
    title: "Combined Height and Width Reduction",
    body: "Addresses both projection and diameter in a single procedure. Most patients with enlarged male nipples benefit from combined correction for a proportionate result. Dr. Jain assesses both dimensions during consultation and plans the correction accordingly.",
  },
  {
    title: "Areola Reduction",
    body: "When the areola is disproportionately large alongside the nipple, areola reduction is performed simultaneously. A circular incision around the areola removes excess pigmented tissue and reduces the overall areola diameter. The incision sits at the areola border where it fades to near-invisible over time.",
  },
  {
    title: "Nipple Reduction with Gynecomastia Surgery",
    body: "Patients with both excess chest glandular tissue and prominent nipples benefit from combining gynecomastia surgery and nipple reduction in a single procedure. Complete chest contouring is achieved in one operation with one recovery period. Dr. Jain plans both components together during consultation.",
  },
];

function TypesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Types of Male Nipple Reduction Surgery</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Male Nipple Reduction is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 30 to 60 minutes under local anesthesia. No hospital admission is required. Dr. Jain marks the planned
              reduction on the nipple before any incision is made, confirming the degree of correction with the patient.
            </p>
            <p>
              For height reduction, an incision is made at the tip of the nipple and excess tissue is removed from the nipple body.
              The remaining tissue is sutured flat. For width reduction, a wedge of tissue is excised from the nipple circumference
              and the edges are sutured to a smaller diameter. Combined reduction addresses both in the same procedure.
            </p>
            <p>
              Incisions sit within the natural transition between the nipple and areola surface. No external dressings are required
              beyond a small protective cover for the first 24 hours. Most patients leave the clinic within an hour of arrival.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 2",
      description: "Mild swelling and minimal discomfort at the nipple site. Normal and expected. Managed with standard pain relief.",
      icon: BedDouble,
    },
    {
      period: "Days 3 to 5",
      description: "Most patients resume normal daily activity. Loose clothing recommended to avoid pressure on the chest.",
      icon: Monitor,
    },
    {
      period: "Week 1",
      description: "Sutures dissolve or are removed at the post-operative visit. The reduction is visible even through early swelling.",
      icon: Clock,
    },
    {
      period: "Weeks 2 to 4",
      description: "Full healing. The final nipple shape and size are visible. Gym and strenuous chest activity resume after 2 to 3 weeks.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Male Nipple Reduction?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are adult men in good general health with a specific concern about nipple size, projection, or visibility
              through clothing. There is no upper age limit. The procedure is equally effective across all adult age groups. Patients
              do not need to have extreme nipple prominence to benefit. Men who are fit and lean but whose nipples remain visible
              through clothing, or who feel self-conscious removing their shirt, are typical candidates.
            </p>
            <p>
              Patients with gynecomastia alongside nipple prominence are assessed for both concerns during consultation. In many cases,
              treating only the nipple while leaving significant glandular tissue is not the most complete solution. Dr. Jain advises
              on whether nipple reduction alone addresses the concern or whether combined surgery produces a better overall chest
              result. Patients at a stable weight get the most predictable outcome as weight fluctuations can affect surrounding chest
              tissue after surgery.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const COMPARISON_ROWS = [
  {
    label: "What It Targets",
    nipple: "The nipple only. Size, projection, and diameter.",
    gyno: "The entire chest. Excess glandular tissue and fat beneath the nipple and across the chest.",
  },
  {
    label: "Invasiveness",
    nipple: "Minimal. Local anesthesia, no hospital admission.",
    gyno: "Moderate. Liposuction and gland excision, longer procedure.",
  },
  {
    label: "Recovery",
    nipple: "2 to 3 days to normal activity.",
    gyno: "1 to 2 weeks to normal activity.",
  },
  {
    label: "Best For",
    nipple: "Men whose concern is nipple size and visibility with no significant chest glandular excess.",
    gyno: "Men with puffy or enlarged chest from glandular tissue or fat, with or without nipple prominence.",
  },
];

function ComparisonSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="comparison-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Male Nipple Reduction vs. Gynecomastia Surgery
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[28%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Nipple Reduction
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Gynecomastia Surgery
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.nipple}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.gyno}</td>
                </tr>
              ))}
              <tr>
                <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                  Combined
                </th>
                <td colSpan={2} className="py-4 px-3 text-deep-navy/70 align-top">
                  Frequently performed together for complete chest contouring in a single procedure.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {MALE_NIPPLE_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`male-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Male Nipple Reduction Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses nipple projection, width, and chest anatomy to determine which reduction
          technique addresses your specific concern. If gynecomastia is also present, he will advise on whether combined correction
          produces a better overall result. You leave with a clear plan and realistic expectations.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Gynecomastia Surgery",
      description: "Complete chest contouring combining gland removal and nipple reduction in one procedure.",
      href: "/gynecomastia-surgery-mumbai",
    },
    {
      name: "Buccal Fat Removal",
      description: "Facial contouring for men seeking definition alongside chest aesthetics.",
      href: "/buccal-fat-removal-mumbai",
    },
    {
      name: "Rhinoplasty",
      description: "Facial balance and proportion for men considering multiple aesthetic refinements.",
      href: "/rhinoplasty-in-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function MaleNippleReductionPage() {
  return (
    <>
      <SEO
        title="Male Nipple Reduction Surgery | Dr. Vicky Jain"
        description="Enlarged or protruding nipples visible through clothing. Dr. Vicky Jain performs male nipple reduction under local anesthesia. Book your consultation."
        canonical="/male-nipple-reduction-mumbai"
      />
      <JsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <CorrectsSection />
      <TypesSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <CandidateSection />
      <ComparisonSection />
      <FAQSection />
      <CTASection />
      <PatientTestimonials procedureName="Gynecomastia" />
      <RelatedProceduresSection />
    </>
  );
}
